import { useEffect, useState, useCallback } from "react";
import { Pencil } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";
import SlidePanel from "../../components/admin/SlidePanel";
import { TextField, TextareaField, SelectField, FormFooterActions } from "../../components/admin/FormFields";
import ImageUpload from "../../components/admin/ImageUpload";

const EMPTY = {
  id: "",
  slug: "",
  name: "",
  category: "residential",
  short_description: "",
  long_description: "",
  image_url: "",
  icon: "LayoutGrid",
  order: 0,
};

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60);

// Icons available in Home.jsx — keep in sync
const ICONS = [
  "LayoutGrid", "Layers", "Sofa", "Trees", "Square", "Flame", "Columns", "Mountain",
  "Building2", "Leaf", "HardHat", "Snowflake", "Droplets", "Construction",
].map((i) => ({ value: i, label: i }));

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/services").then((r) => setItems(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const open = (item) => setEditing(item ? { ...item } : { ...EMPTY });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.name || !editing.short_description) {
      toast.error("Name and Short Description are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, slug: editing.slug || slugify(editing.name) };
      await api.post("/admin/services", payload);
      toast.success("Service saved");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing.id) return close();
    if (!window.confirm(`Delete "${editing.name}"?`)) return;
    try {
      await api.delete(`/admin/services/${editing.id}`);
      toast.success("Service deleted");
      close();
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const residential = items.filter((s) => s.category === "residential");
  const commercial = items.filter((s) => s.category === "commercial");

  const Group = ({ heading, list }) => (
    <div className="mb-10">
      <div className="overline text-[#8C1D30] mb-4">{heading}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((s) => (
          <button
            key={s.id}
            data-testid={`service-row-${s.id}`}
            onClick={() => open(s)}
            className="group flex gap-4 bg-[#151515] border border-[#D8D6D0]/10 hover:border-[#D8D6D0]/30 transition-colors p-4 text-left"
          >
            <img src={s.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${s.image_url}` : s.image_url} alt={s.name} className="w-20 h-20 object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="font-serif-display text-lg truncate">{s.name}</div>
                <Pencil className="w-4 h-4 text-[#D8D6D0]/30 group-hover:text-[#F5F3EF] flex-shrink-0" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#D8D6D0]/40 mt-1">{s.slug}</div>
              <p className="text-xs text-[#D8D6D0]/60 mt-2 line-clamp-2">{s.short_description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-services">
      <AdminHeader
        title="Services"
        subtitle="The catalog of services you offer. Each becomes a SEO landing page combined with each area."
        action="Add Service"
        actionTestId="add-service-btn"
        onAction={() => open(null)}
        count={items.length}
      />

      {residential.length > 0 && <Group heading="Residential" list={residential} />}
      {commercial.length > 0 && <Group heading="Commercial" list={commercial} />}

      <SlidePanel
        open={!!editing}
        onClose={close}
        title={editing?.id ? "Edit Service" : "New Service"}
        subtitle={editing?.id ? `Slug: ${editing.slug}` : ""}
        footer={
          <FormFooterActions
            onCancel={close}
            onSubmit={save}
            onDelete={editing?.id ? remove : null}
            submitting={saving}
          />
        }
      >
        {editing && (
          <>
            <TextField id="name" label="Service Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v, slug: editing.slug || slugify(v) })} required testId="service-name" />
            <TextField id="slug" label="URL Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: slugify(v) })} hint="Used in URLs: /areas/{area}/{slug}" />
            <SelectField
              id="category"
              label="Category"
              value={editing.category}
              onChange={(v) => setEditing({ ...editing, category: v })}
              options={[
                { value: "residential", label: "Residential" },
                { value: "commercial", label: "Commercial" },
              ]}
              required
            />
            <TextField id="short_description" label="Short Description" value={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} required hint="One-liner shown on Services list." />
            <TextareaField id="long_description" label="Long Description" value={editing.long_description} onChange={(v) => setEditing({ ...editing, long_description: v })} rows={5} />
            <SelectField id="icon" label="Icon" value={editing.icon} onChange={(v) => setEditing({ ...editing, icon: v })} options={ICONS} hint="Lucide-react icon name." />
            <TextField id="order" label="Display Order" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v || 0 })} type="number" />
            <ImageUpload label="Service Image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testId="service-image" />
          </>
        )}
      </SlidePanel>
    </div>
  );
}
